import Logo from "./Logo"
import AppHead from "./AppHead"
import AppNavMotion from "./AppNavMotion"
import {logout} from "@/lib/auth/client"
import Link from "next/link"
import {useRouter} from 'next/router'
import {useEffect, useState, ReactNode, useMemo} from "react"
import CloseIcon from "@material-ui/icons/Close"
import MessageIcon from "@material-ui/icons/Message"
import {trpc} from "@/lib/requests/index"
import { useSessionStorage } from "@/lib/hooks/useSessionStorage"
import NextMsgIcon from "@material-ui/icons/ArrowForwardIos"
import PreviousMsgIcon from "@material-ui/icons/ArrowBackIos"

export type AppLayoutProps = {
  children?: ReactNode
  pageTitle?: string
  isAdmin?: boolean
  showNav?: boolean
}

export default function AppLayout(
  props: AppLayoutProps
): JSX.Element {
  const {
    children = null,
    pageTitle = "Pipeline Pro Tools",
    isAdmin = false,
    showNav = true,
  } = props

  const router = useRouter()

  const billingStateQuery = trpc.useQuery(
    ["billing.checkIsOverdue"], {enabled: false}
  )
  const data = billingStateQuery.data

  useEffect(() => { billingStateQuery.refetch() }, [])

  const [showMessages, setShowMessages] = useState(false)
  // messages are raw html, not just strings
  const [messages, setMessages] = useState<string[]>([])
  const [fetchedMessages, setFetchedMessages] = useState(false)

  const [lastLogin, setLastLogin] = useState("")
  const adminMessagesQuery = trpc.useQuery(
    ["user.adminMessages", {dateString: lastLogin}], 
    {enabled: false}
  )
  const messageLoading = adminMessagesQuery.isLoading
  const messageData = adminMessagesQuery.data || []

  useEffect(() => {
    if (!data || fetchedMessages) {
      return
    }
    const {isOverdue, lastLogin} = data
    if (isOverdue) {
      return
    }
    setLastLogin(String(lastLogin))
    setTimeout(() => { adminMessagesQuery.refetch() }, 20)
    setFetchedMessages(true)
  }, [data])

  const [hasSeenMsgs, setHasSeenMsgs] = useSessionStorage("seen-msgs", false)

  useEffect(() => {
    if (hasSeenMsgs || messageLoading || messageData.length < 1) {
      return
    }
    setMessages(messageData.map((m) => m.html))
    setShowMessages(true)
  }, [messageData])

  // if user balance is overdue
  // always route user to billing page
  // essentially lock down the app - so that only billing information can be changed
  useEffect(() => {
    if (!data) {
      return
    }
    const isOverdue = data.isOverdue
    const targetUrl = "/account/billing"
    if (isOverdue && router.pathname !== targetUrl) {
      console.warn("account locked down due to overdue balance routing to billing page")
      router.push(targetUrl)
      const milliseconds = 200
      window.setTimeout(() => {
        window.alert("Your account is locked due to an outstanding bill! Please provide a valid payment method to unlock your account.")
      }, milliseconds)
    }
  }, [router.query.counter, data])

  const showBreadcrumbs = router.pathname !== "/app"

  const trpcContext = trpc.useContext()

  function onLogout() {
    trpcContext.invalidateQueries()
    logout()
  }

  const msgsSeenMut = trpc.useMutation([
    "user.msgsSeen"
  ])

  function onCloseMsgs() {
    setShowMessages(false)
    setHasSeenMsgs(true)
    msgsSeenMut.mutate()
  }

  return <>
    <AppHead title={pageTitle}/>
    
    <div className="relative flex flex-col z-10">

      {showMessages ? <MsgModal
        messages={messages}
        onCloseMsgs={onCloseMsgs}
      /> : <></>}
      
      <header className="w-full flex flex-row items-center justify-between px-4 pt-2">
        <Link href="/app">
          <a><Logo className="h-16"/></a>
        </Link>

        <div className="flex items-center">
          {isAdmin ? <>
            <Link href="/admin">
              <a
                className={
                  "text-2xl font-display mr-4" +
                  " text-purple-700 hover:text-ppt-success"
                }
              >
                {"admin"}
              </a>
            </Link>
          </> : null}
          <Link href="/account">
            <a
              className={
                "text-2xl font-display mr-4" +
                " text-ppt-blue hover:text-ppt-success"
              }
            >
              {"account"}
            </a>
          </Link>
          <button
            className="text-2xl text-ppt-graytext font-display hover:text-red-700"
            onClick={onLogout}
          >
            {"logout"}
          </button>
        </div>
      </header>
      
      <main className="flex w-full z-0 relative flex-col-reverse sm:flex-row flex-grow">
        {showNav ? <AppNavMotion/> : null}
        
        <div className="mx-auto overflow-hidden w-4/5">
          {showBreadcrumbs ? <div
            style={{width: "90%"}}
            className="mx-auto flex items-center justify-center"
          >
            <UrlBreadCrumbs/>
          </div> : null}
          
          {children}
        </div>
        
      </main>
    </div>
  </>
}

type BreadCrumb = {
  text: string
  href?: string
}

type UrlBreadCrumbsProps = {
  className?: string
}

function UrlBreadCrumbs(props: UrlBreadCrumbsProps): JSX.Element {
  const router = useRouter()

  const breadcrumbs = useMemo(() => {
    const crumbs: BreadCrumb[] = []
    const parsedPath = router.asPath.split("/")
    let compiledPath = ""
    for (let i = 0; i < parsedPath.length; i++) {
      let path = parsedPath[i]
      
      let href = ""
      const basePath = path.length < 1
      if (basePath) {
        path = "home"
        href = "/app"
      } else {
        compiledPath += `/${path}`
      }

      if (compiledPath !== router.pathname && !basePath) {
        href = compiledPath
      }

      let text = path
      const hasUrlQuery = text.includes("?")
      if (hasUrlQuery) {
        const [urlPath, _query] = text.split("?")
        text = urlPath
      }
      crumbs.push({text, href})
    }
    return crumbs
  }, [router.pathname])
  
  return <div className={`flex flex-wrap text-sm text-center ${props.className || ""}`}>
    {breadcrumbs.map(((crumb, index) => {
      const isLastCrumb = index === breadcrumbs.length - 1
      const target = crumb.href ? crumb.href : undefined
      return <Link
        key={crumb.href || ""}
        href={target || ""}
      >
        <a
          className={`block hover:text-gray-200 ${isLastCrumb ? "text-gray-200" : "text-blue-500"}`}
          href={target}
          title={isLastCrumb ? "current page" : undefined}
        >
          {crumb.text}

          {isLastCrumb ? null : <span
            className="mx-1 text-gray-600"
          >
            {">"}
          </span>}
        </a>
      </Link>
    }))}
  </div>
}

type MsgModalProps = {
  messages: string[]
  onCloseMsgs: () => unknown
}

function MsgModal(props: MsgModalProps): JSX.Element {
  const [messageIndex, setMessageIndex] = useState(0)
  
  const {messages} = props

  return <>
    <div className="z-30 fixed left-0 top-0">
      <div className="w-screen h-screen flex items-center justify-center bg-overlay shadow-lg">
          <div className="relative text-gray-800 max-w-lg w-4/5">

            <div className="bg-gray-400 w-full flex items-center align-middle px-2 py-1 rounded-t">
              <div className="w-1/2">
                <span className="text-blue-800"><MessageIcon/></span>
              </div>

              <div 
                  className="w-1/2 text-right"
                  onClick={props.onCloseMsgs}
              >
                  <span className="text-red-500 cursor-pointer"><CloseIcon/></span>
              </div>
            </div>

            <div className="bg-gray-100 px-2 py-2 rounded-b">
              <div className="text-xs text-gray-600 mb-1">
                {"message "} 
                <span className="text-purple-500">
                  {messageIndex + 1}
                </span>
                {` of ${messages.length}`}
              </div>

              <div dangerouslySetInnerHTML={{__html: messages[messageIndex]}}/>

              <div className={`text-right text-sm mt-2 ${messages.length < 2 ? "hidden" : ""}`}>
                  <span 
                    className={`mr-2 cursor-pointer hover:text-blue-600 ${messageIndex < 1 ? "invisible" : ""}`}
                    onClick={() => setMessageIndex((index) => index - 1)}
                    title="Previous Message"
                  >
                    <PreviousMsgIcon/>
                  </span>

                  <span 
                    className={`hover:text-blue-600 cursor-pointer ${messageIndex + 1 >= messages.length ? "invisible" : ""}`}
                    onClick={() => setMessageIndex((index) => index + 1)}
                    title="Next Message"
                  >
                    <NextMsgIcon/>
                  </span>
              </div>
            </div>
          </div>
      </div>
    </div>
  </>
}